import type { ContentEntry } from "./schema";

/**
 * Détection des couples réellement confondables (EDT-004).
 *
 * Le risque numéro un du projet est un corpus médiocre (README §2), et sa forme
 * la plus cruelle est celle-ci : le joueur écrit une réponse parfaitement juste,
 * le jeu la refuse. Cela ne peut arriver que si deux entrées ont **la même
 * longueur** et des définitions que rien ne sépare vraiment.
 *
 * La première relecture avait signalé six couples à la main — `désir`/`envie`,
 * `store`/`volet`, `écharpe`/`foulard`… Elle en avait manqué. Un couple comme
 * `centimètre`/`millimètre`, dix lettres chacun, dont les définitions ne
 * diffèrent que par « 0,01 » et « 0,001 », est indevinable pour un joueur.
 *
 * **Ce que cet outil produit est une liste à relire, pas un verdict.** Un sac de
 * mots ne comprend pas la négation : `fauteuil` « muni d'un dossier » et
 * `tabouret` « sans dossier » se distinguent parfaitement pour un humain, et
 * pas pour lui. Il resserre le champ de 1 510 entrées à quelques dizaines de
 * couples ; le jugement reste éditorial.
 */

/**
 * Mots trop communs pour discriminer quoi que ce soit. Les garder ferait
 * ressembler toutes les définitions entre elles.
 */
const STOP_WORDS = new Set([
  "dans", "pour", "avec", "sans", "plus", "tres", "cette", "celui", "celle", "leur", "leurs",
  "dont", "elle", "etre", "fait", "faire", "ayant", "ainsi", "souvent", "generalement", "general",
  "autre", "autres", "quelque", "chose", "quelquun", "personne", "maniere", "facon", "partie",
  "ensemble", "permet", "servant", "destine", "utilise", "designe", "petit", "petite", "grand",
  "grande", "longue", "long", "principalement", "notamment", "parfois", "toujours", "jamais",
  "lorsque", "comme", "entre", "selon",
]);

/**
 * Un mot de trois lettres ne porte pas de sens ici. Quatre, si : `pain`, `bois`,
 * `sang` et `os` sont précisément ce qui sépare deux artisans ou deux organes.
 */
const MIN_WORD_LENGTH = 4;

export type AmbiguityOptions = {
  /** Part minimale de vocabulaire commun pour qu'un couple soit examiné. */
  readonly minOverlap?: number;
  /** Au-delà de ce nombre de mots propres à chacun, le couple se tranche seul. */
  readonly maxDiscriminating?: number;
};

export type AmbiguityRisk = {
  readonly normalizedLength: number;
  /** Part du vocabulaire du plus court des deux qui figure aussi dans l'autre. */
  readonly overlap: number;
  /** Nombre de mots propres au moins fourni des deux : c'est de quoi trancher. */
  readonly discriminating: number;
  readonly first: ContentEntry;
  readonly second: ContentEntry;
  readonly firstOnly: readonly string[];
  readonly secondOnly: readonly string[];
};

/** Diacritiques combinants, en échappement explicite : le fichier reste lisible. */
const COMBINING_MARKS = /[̀-ͯ]/g;

function fold(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(COMBINING_MARKS, "");
}

/** Longueur normalisée : ni accent, ni tiret, ni apostrophe, ni espace. */
export function normalizedLengthOf(answer: string): number {
  return fold(answer).replace(/[^a-z]/g, "").length;
}

function contentWords(definition: string): ReadonlySet<string> {
  return new Set(
    fold(definition)
      .split(/[^a-z]+/)
      .filter((word) => word.length >= MIN_WORD_LENGTH && !STOP_WORDS.has(word)),
  );
}

/**
 * Couples de même longueur dont les définitions se recouvrent fortement **et**
 * n'offrent presque rien pour trancher. Les deux conditions comptent : un fort
 * recouvrement seul ne dit que « même champ sémantique », ce qui est banal.
 */
export function findAmbiguityRisks(
  entries: readonly ContentEntry[],
  options: AmbiguityOptions = {},
): readonly AmbiguityRisk[] {
  const minOverlap = options.minOverlap ?? 0.5;
  const maxDiscriminating = options.maxDiscriminating ?? 2;

  const byLength = new Map<number, { entry: ContentEntry; words: ReadonlySet<string> }[]>();
  for (const entry of entries) {
    const length = normalizedLengthOf(entry.answer);
    const words = contentWords(entry.definition);
    if (words.size === 0) continue;

    const group = byLength.get(length);
    if (group === undefined) byLength.set(length, [{ entry, words }]);
    else group.push({ entry, words });
  }

  const risks: AmbiguityRisk[] = [];

  for (const [normalizedLength, group] of byLength) {
    for (let i = 0; i < group.length; i += 1) {
      for (let j = i + 1; j < group.length; j += 1) {
        const a = group[i];
        const b = group[j];
        if (a === undefined || b === undefined) continue;

        const shared = [...a.words].filter((word) => b.words.has(word));
        const firstOnly = [...a.words].filter((word) => !b.words.has(word));
        const secondOnly = [...b.words].filter((word) => !a.words.has(word));

        const overlap = shared.length / Math.min(a.words.size, b.words.size);
        const discriminating = Math.min(firstOnly.length, secondOnly.length);

        if (overlap < minOverlap || discriminating > maxDiscriminating) continue;

        risks.push({
          normalizedLength,
          overlap,
          discriminating,
          first: a.entry,
          second: b.entry,
          firstOnly,
          secondOnly,
        });
      }
    }
  }

  // Le plus urgent d'abord : ce qui ne se tranche par rien.
  return risks.sort(
    (x, y) => x.discriminating - y.discriminating || y.overlap - x.overlap,
  );
}

/**
 * Un couple relu par un humain et jugé distinct, avec les deux définitions
 * telles qu'elles étaient ce jour-là.
 *
 * Le détecteur est lexical : « avec dossier » et « sans dossier » lui paraissent
 * identiques. Sans cette liste, vingt fausses alertes permanentes noyaient la
 * vraie suivante. L'examen ne vaut que pour les définitions examinées : dès que
 * l'une change, le couple ressort.
 */
export type ExaminedPair = {
  readonly mots: readonly [string, string];
  readonly definitions: readonly [string, string];
  readonly motif: string;
};

function pairSignature(a: { answer: string; definition: string }, b: { answer: string; definition: string }): string {
  return JSON.stringify([[a.answer, a.definition], [b.answer, b.definition]].sort());
}

export function withoutExaminedPairs(
  risks: readonly AmbiguityRisk[],
  examined: readonly ExaminedPair[],
): readonly AmbiguityRisk[] {
  const known = new Set(
    examined.map(({ mots, definitions }) =>
      pairSignature(
        { answer: mots[0], definition: definitions[0] },
        { answer: mots[1], definition: definitions[1] },
      ),
    ),
  );
  return risks.filter(({ first, second }) => !known.has(pairSignature(first, second)));
}
